import { generatePath, NavLink } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  ButtonSize,
  ToggleInput,
  ToggleSize,
  IconBlock,
  IconSelected,
  PermissionEnum,
  ButtonSecondary,
  IconEdit,
  formatBalance,
  IconChangeLoClock,
  IconArrowRight,
  IconInfo,
  IconSandWatch,
  IconCloseCircle,
  IconSelectedCircle,
  IconAttention,
  IconLoader,
} from '@ui/src';
import { useAppSelector } from '@store';
import Card from '../partial/card/card';
import InfoRow from './info-row';
import {
  getPspPartnerLocation,
  getPspAccountCurrencyType,
  getPspPartnerType,
} from '../../hoc/bank-and-psp/helpers/get-enums-names';
import { BankRoutes } from '../../routes';
import { AccountGeneralInfoTypes, CurrencyAndBalance } from './types';

import cn from './info.module.css';

const getVaultStatus = (status: string) => {
  let vaultStatus;
  let vaultStatusIcon;

  switch (status) {
    case 'IN_PROGRESS':
      vaultStatus = 'Linking';
      vaultStatusIcon = <IconSandWatch width={14} height={14} />;
      break;
    case 'FAILED':
      vaultStatus = 'Failed';
      vaultStatusIcon = <IconCloseCircle className={cn.failedStatus} />;
      break;
    case 'DONE':
      vaultStatus = 'Linked';
      vaultStatusIcon = <IconSelectedCircle className={cn.doneStatus} />;
      break;
    default:
      break;
  }

  return { vaultStatus, vaultStatusIcon };
};

const GeneralInfo = ({
  isLoading,
  generalInfo,
  changeVisibility,
  onStatusChange,
  pspGeneralInfo,
  countries,
  accountId,
  statusChangeInProgress,
  visiblityChangeInProgress,
  currentBalances,
  overdraftEnabled,
  auditLogAvailable,
  casp,
  changeExclude,
  setBindSidepanelIsOpen,
  changeDefaultState,
  defaultStateChangeInProgress,
  search,
}: AccountGeneralInfoTypes) => {
  const [currenciesAndBalances, setCurrenciesAndBalances] = useState<
    CurrencyAndBalance[] | undefined
  >(undefined);
  const { activePermissions } = useAppSelector(
    (state) => state.configurationDictionaries,
  );
  const { pspAccountsCategories } = useAppSelector(
    (state) => state.bankAndPsp.info,
  );

  useEffect(() => {
    if (generalInfo?.currencies && currentBalances) {
      const nextCurrenciesAndBalances: CurrencyAndBalance[] = [];

      generalInfo?.currencies?.forEach((item) => {
        if (
          !nextCurrenciesAndBalances?.find(
            (currency) => currency.currencyId === item.id,
          )
        ) {
          const balance = currentBalances?.find(
            (balanceValue) => balanceValue.currencyId === item.id,
          );

          nextCurrenciesAndBalances.push({
            currency: item,
            currencyId: item.id,
            balance: balance || undefined,
            balanceActive: Boolean(balance),
            currencyActive: true,
          });
        }
      });

      currentBalances?.forEach((item) => {
        if (
          !nextCurrenciesAndBalances.find(
            (currency) => currency.currencyId === item.currencyId,
          )
        ) {
          nextCurrenciesAndBalances.push({
            currency: item.currency,
            currencyId: item.id,
            balance: item || undefined,
            balanceActive: true,
            currencyActive: false,
          });
        }
      });

      setCurrenciesAndBalances(nextCurrenciesAndBalances);
    }
  }, [generalInfo?.currencies, currentBalances]);

  let buttonText;
  let buttonIcon;

  if (statusChangeInProgress) {
    buttonText = 'Loading...';
  } else {
    buttonText = `${generalInfo?.active ? 'Deactivate' : 'Activate'} Account`;
  }

  if (generalInfo?.active) {
    buttonIcon = <IconBlock />;
  } else {
    buttonIcon = <IconSelected />;
  }

  let caspValue: string | React.JSX.Element = '-';

  if (generalInfo?.caspId) {
    caspValue = casp?.find((item) => item.value === generalInfo?.caspId)
      ?.name as string;
  } else if (!generalInfo?.caspId && pspGeneralInfo?.caspId) {
    caspValue = (
      <div className={cn.caspDefaultFromBank}>
        {casp?.find((item) => item.value === pspGeneralInfo?.caspId)?.name}{' '}
        <span>(Default from Bank)</span>
      </div>
    );
  }

  return (
    <Card
      isLoading={isLoading}
      title={
        <>
          <div className={cn.generalInfoTitleWrapper}>
            <div className={cn.generalInfoTitleLeft}>
              <div className={cn.generalInfoTitle}>
                {`Account No: ${generalInfo?.account?.fullNumber}`}
              </div>
              <div
                className={`${cn.generalInfoStatus} ${
                  generalInfo?.active ? cn.generalInfoStatusActive : ''
                }`}
              >
                {generalInfo?.active ? <IconSelected /> : <IconBlock />}
                {generalInfo?.active ? 'Active' : 'Disabled'}
              </div>
              {generalInfo?.account?.overdraftEnabled && overdraftEnabled && (
                <div className={cn.overdraft}>Overdraft</div>
              )}
            </div>
            <div className={cn.visibleForClientsWrapper}>
              <ToggleInput
                title="Visible by clients"
                name="toggle-check"
                size={ToggleSize.SMALL}
                onChange={() => changeVisibility && changeVisibility(true)}
                checked={generalInfo?.visibleToClient}
                disabled={
                  !activePermissions?.includes(
                    PermissionEnum.PSP_ACCOUNT_EDIT_ACTION,
                  ) || visiblityChangeInProgress
                }
              />

              <div className={cn.excludeFromTopUpWrapper}>
                <ToggleInput
                  title="Exclude For External Account"
                  name="excludeFromTopUpIfExternalAccountAvailable"
                  size={ToggleSize.SMALL}
                  onChange={() => changeExclude(true)}
                  checked={
                    generalInfo?.excludeFromTopUpIfExternalAccountAvailable
                  }
                  disabled={
                    !activePermissions?.includes(
                      PermissionEnum.PSP_ACCOUNT_EDIT_ACTION,
                    ) || visiblityChangeInProgress
                  }
                />
                <div className={cn.excludeHint}>
                  <IconInfo width={18} height={18} />
                  <div className={cn.excludeHintContent}>
                    Exclude From Top Up If External Account Available
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={cn.useAsDefaultWrapper}>
            <ToggleInput
              title="Use as Default for external operations"
              name="isDefaultForExternalOperations"
              size={ToggleSize.SMALL}
              onChange={() => changeDefaultState(true)}
              checked={generalInfo?.isDefaultForExternalOperations}
              disabled={
                !activePermissions?.includes(
                  PermissionEnum.PSP_ACCOUNT_EDIT_ACTION,
                ) || defaultStateChangeInProgress
              }
            />
          </div>

          <div className={cn.generalInfoControls}>
            <div className={cn.generalInfoControlsLeft}>
              {activePermissions?.includes(
                PermissionEnum.PSP_ACCOUNT_DELETE_ACTION,
              ) && (
                <ButtonSecondary
                  onClick={() => onStatusChange(!generalInfo?.active)}
                  size={ButtonSize.SMALL}
                  text={buttonText}
                  icon={!statusChangeInProgress ? buttonIcon : undefined}
                  className={cn.changeStatusButton}
                />
              )}
              {activePermissions?.includes(
                PermissionEnum.PSP_ACCOUNT_EDIT_ACTION,
              ) &&
                activePermissions?.includes(
                  PermissionEnum.PSP_ACCOUNT_DELETE_ACTION,
                ) && <div className={cn.separator} />}
              {activePermissions?.includes(
                PermissionEnum.PSP_ACCOUNT_EDIT_ACTION,
              ) && (
                <NavLink
                  to={`${generatePath(
                    BankRoutes.BANK_AND_PSP_ACCOUNT_PAGE_EDIT,
                    {
                      accountId: accountId as string,
                    },
                  )}${search || ''}`}
                  className={cn.infoTitleLink}
                >
                  <span className={cn.infoTitleLinkIcon}>
                    <IconEdit width={15} height={15} />
                  </span>
                  <span className={cn.infoTitleLinkText}>Edit</span>
                </NavLink>
              )}
            </div>
            <div className={cn.generalInfoControlsRight}>
              {auditLogAvailable && (
                <div className={cn.changeLogLinksWrapper}>
                  <NavLink
                    to={`/administration/change-logs?entityType=PSP_ACCOUNT&entityId=${generalInfo?.id}`}
                    target="_blank"
                    className={cn.changeLogLinks}
                  >
                    <IconChangeLoClock />
                    Change Log
                  </NavLink>
                </div>
              )}
              <div className={cn.accountLinkWrapper}>
                <NavLink
                  to={`/accounts/list/${generalInfo?.account?.id}`}
                  target="_blank"
                  className={`${cn.changeLogLinks} ${cn.accountsLink}`}
                >
                  Open in Accounts
                  <IconArrowRight width={11} height={10} />
                </NavLink>
              </div>
            </div>
          </div>
        </>
      }
    >
      <>
        <div className={cn.generalInfoBlock}>
          <div className={cn.generalInfoBlockContent}>
            <div className={cn.generalInfoBlockContentTitle}>
              Account Details
            </div>
            <InfoRow
              title="Acc. No. / IBAN"
              value={generalInfo?.realNumberOrIban}
            />
            {pspGeneralInfo?.pspIntegration &&
              pspGeneralInfo?.pspIntegration.externalPspPartnerAccountCreate &&
              (pspGeneralInfo?.pspIntegration?.name === 'PRIME_VAULT_WALLET' ||
                pspGeneralInfo?.pspIntegration?.name === 'OPEN_PAYD' ||
                pspGeneralInfo?.pspIntegration?.name === 'Keel') &&
              (generalInfo?.externalAccountBindType ||
                generalInfo?.externalAccountId) && (
                <>
                  <InfoRow
                    title={
                      pspGeneralInfo?.pspIntegration?.name ===
                      'PRIME_VAULT_WALLET'
                        ? 'Vault'
                        : 'External Account'
                    }
                    value={
                      generalInfo?.externalAccountId ? (
                        <NavLink
                          to={`${generatePath(
                            BankRoutes.BANK_AND_PSP_ACCOUNT_PAGE_EXTERNAL_ACCOUNT,
                            {
                              accountId: accountId as string,
                              externalAccountId: generalInfo?.externalAccountId,
                            },
                          )}${search || ''}`}
                          className={cn.vaultDetailsButton}
                        >
                          Details
                        </NavLink>
                      ) : (
                        <button
                          type="button"
                          className={cn.showBindSidepanelButton}
                          onClick={() => setBindSidepanelIsOpen(true)}
                          disabled={
                            generalInfo?.pspIntegrationAccountStatus ===
                            'IN_PROGRESS'
                          }
                        >{`Link ${
                          pspGeneralInfo?.pspIntegration?.name ===
                          'PRIME_VAULT_WALLET'
                            ? 'Vault'
                            : 'External Account'
                        }`}</button>
                      )
                    }
                  />
                  <InfoRow
                    title={
                      pspGeneralInfo?.pspIntegration?.name ===
                      'PRIME_VAULT_WALLET'
                        ? 'Vault status'
                        : 'External Account Status'
                    }
                    value={
                      <div className={cn.vaultStatus}>
                        {generalInfo?.pspIntegrationAccountStatus ? (
                          <div className={cn.vaultStatusContent}>
                            <div className={cn.vaultStatusIcon}>
                              {
                                getVaultStatus(
                                  generalInfo?.pspIntegrationAccountStatus,
                                ).vaultStatusIcon
                              }
                            </div>

                            {
                              getVaultStatus(
                                generalInfo?.pspIntegrationAccountStatus,
                              ).vaultStatus
                            }
                          </div>
                        ) : (
                          '-'
                        )}
                      </div>
                    }
                  />
                </>
              )}
            <InfoRow
              title="Location"
              value={getPspPartnerLocation(pspGeneralInfo?.location as string)}
            />
            <InfoRow
              title="Opened in"
              value={getPspPartnerType(pspGeneralInfo?.partnerType as string)}
            />
            <InfoRow
              title="Type"
              value={
                pspAccountsCategories?.find(
                  (item) => item.id === generalInfo?.categoryId,
                )?.name
              }
            />
            <InfoRow
              title="Currency Type"
              value={getPspAccountCurrencyType(
                generalInfo?.currencyType as string,
              )}
            />
            <InfoRow title="CASP" value={caspValue} />
            {generalInfo?.intermediaryBankName && (
              <InfoRow
                title="Intermediary Bank Name"
                value={generalInfo?.intermediaryBankName}
              />
            )}
            {generalInfo?.intermediaryBankSwift && (
              <InfoRow
                title="Intermediary Bank SWIFT"
                value={generalInfo?.intermediaryBankSwift}
              />
            )}
          </div>
          <div className={cn.generalInfoBlockContent}>
            <div className={cn.generalInfoBlockContentTitle}>
              Bank details
              <div
                className={`${cn.bankDetailsHint} ${generalInfo?.useOwnBankDetails ? cn.override : ''}`}
              >
                {generalInfo?.useOwnBankDetails && (
                  <IconEdit width={14} height={14} />
                )}
                {generalInfo?.useOwnBankDetails
                  ? 'Overridden on this account'
                  : 'From Bank'}
              </div>
            </div>
            <InfoRow
              title="Bank Name"
              value={
                generalInfo?.useOwnBankDetails
                  ? (generalInfo?.bankName as string)
                  : pspGeneralInfo?.companyName
              }
            />
            <InfoRow
              title="Legal Name"
              value={
                generalInfo?.useOwnBankDetails
                  ? (generalInfo?.bankLegalName as string)
                  : pspGeneralInfo?.legalName
              }
            />
            <InfoRow
              title="Bank Country"
              value={
                countries?.find((item) =>
                  generalInfo?.useOwnBankDetails
                    ? item.value === generalInfo?.bankCountryId
                    : item.value === pspGeneralInfo?.countryId,
                )?.name
              }
            />
            <InfoRow
              title="Bank Address"
              value={
                generalInfo?.useOwnBankDetails
                  ? (generalInfo?.bankAddress as string)
                  : pspGeneralInfo?.address
              }
            />

            <InfoRow
              title="SWIFT / BIC"
              value={
                generalInfo?.useOwnBankDetails
                  ? (generalInfo?.bankSwift as string) || '-'
                  : pspGeneralInfo?.swift || '-'
              }
            />
            <InfoRow
              title="Sort Code"
              value={
                generalInfo?.useOwnBankDetails
                  ? (generalInfo?.bankSortCode as string) || '-'
                  : pspGeneralInfo?.sortCode || '-'
              }
            />
            <InfoRow
              title="ABA / Routing number"
              value={
                generalInfo?.useOwnBankDetails
                  ? (generalInfo?.bankAba as string) || '-'
                  : pspGeneralInfo?.aba || '-'
              }
            />
            {generalInfo?.accountOfCorrespondentWithIntermediaryBank && (
              <InfoRow
                title="Account Of Correspondent With Intermediary Bank"
                value={generalInfo?.accountOfCorrespondentWithIntermediaryBank}
              />
            )}
          </div>
        </div>
        <div className={cn.generalInfoBlock}>
          <InfoRow
            title="Managers contact details"
            value={pspGeneralInfo?.managerContacts || '-'}
          />
        </div>
        <div className={cn.generalInfoBlock}>
          <InfoRow
            title="Description"
            value={generalInfo?.description || '-'}
          />
        </div>
        <div className={cn.generalInfoBlock}>
          <div className={`${cn.generalInfoBlockContent} ${cn.fullWidth}`}>
            <InfoRow title="Balance & Currencies" />
            <div className={cn.balancesAndCurrenciesWrapper}>
              {!currenciesAndBalances && (
                <div className={cn.currenciesAndBalancesLoader}>
                  <IconLoader />
                </div>
              )}
              {currenciesAndBalances?.map((item) => (
                <div className={cn.currencyItem} key={item.currencyId}>
                  <div className={cn.currencyItemTop}>
                    <div className={cn.currencyName}>
                      {item?.currency?.name}
                    </div>
                    <div className={cn.currencyBalance}>
                      {item?.balance ? (
                        formatBalance(
                          item?.balance?.value || 0,
                          item?.balance?.currency,
                        )
                      ) : (
                        <div className={cn.emptyBalance} />
                      )}
                    </div>
                  </div>
                  <div
                    className={`${cn.currencyItemBottom} ${
                      !item.balanceActive || !item.currencyActive
                        ? cn.notActive
                        : ''
                    }`}
                  >
                    {item.balanceActive && item.currencyActive ? (
                      <div className={cn.currencyItemBalanceActive}>
                        <IconSelected
                          width={14}
                          height={14}
                          className={cn.activeBalanceIcon}
                        />
                        Active
                      </div>
                    ) : (
                      <div className={cn.currencyItemBalanceActive}>
                        <IconAttention
                          width={18}
                          height={18}
                          className={cn.notActiveBalanceIcon}
                        />
                        <span>
                          {!item.balanceActive ? 'Balance' : 'Currency'}
                        </span>
                        &nbsp;not activated
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {overdraftEnabled && generalInfo?.account?.overdraftEnabled && (
            <div className={cn.generalInfoBlockContent}>
              <InfoRow
                title="Overdraft"
                value={
                  <ul className={cn.accountBalancesList}>
                    {currentBalances && currentBalances?.length > 0 ? (
                      currentBalances?.map((item) => (
                        <li key={item.id}>
                          {item?.overdraftEnabled
                            ? `${formatBalance(
                                Number(item?.overdraftValue) || 0,
                                item?.currency,
                              )} ${item?.currency?.name}` || '-'
                            : '-'}
                        </li>
                      ))
                    ) : (
                      <li>-</li>
                    )}
                  </ul>
                }
              />
            </div>
          )}
        </div>
      </>
    </Card>
  );
};

export default GeneralInfo;
